import { useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  useLegacyReceiptImportCommit,
  useLegacyReceiptImportDryRun,
  type LegacyReceiptImportFormat,
  type LegacyReceiptImportPayload,
  type LegacyReceiptImportReport,
  type LegacyReceiptImportSource,
  type LegacyTradingEntity,
} from '@/hooks/use-legacy-receipt-import';
import { extractApiError } from '@/lib/api-error';
import { useT } from '@/i18n/use-t';

export function LegacyReceiptImportPage() {
  const t = useT('admin_ui');
  const dryRun = useLegacyReceiptImportDryRun();
  const commit = useLegacyReceiptImportCommit();
  const [fileName, setFileName] = useState<string | null>(null);
  const [payload, setPayload] = useState<LegacyReceiptImportPayload | null>(null);
  const [report, setReport] = useState<LegacyReceiptImportReport | null>(null);
  const [created, setCreated] = useState<number | null>(null);
  const [skipped, setSkipped] = useState<number>(0);
  const [skippedIds, setSkippedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Blank is deliberate: the server resolves every department's configured
  // location and refuses incomplete or mixed-zone batches. An override is an
  // explicit operator choice, never an inference from a remote browser.
  const [timeZone, setTimeZone] = useState('');
  // YUKI-329: 1C cutover or the previous re:form till's GP export. Both run
  // the same dry-run / commit contract; only the route and the file differ.
  const [format, setFormat] = useState<LegacyReceiptImportFormat>('1c');
  // YUKI-332: no default, and 1C only. The 1C export carries no VAT, and the
  // register tax a till returns each line under follows from what the store
  // traded as. A GP export carries its own per-line TaxRate, and the GP routes
  // refuse a trading entity.
  const [tradingEntity, setTradingEntity] = useState<LegacyTradingEntity | ''>('');
  const entityLabel = (entity: LegacyTradingEntity): string =>
    entity === 'llc'
      ? t('receipts.import.entityLlc', 'LLC (ТОВ) — lines at their catalogue VAT rate')
      : t('receipts.import.entityIndividual', 'Individual entrepreneur (ФОП) — every line at 0 %');
  // Remounts the file input, so switching the source system drops a file
  // chosen for the other one instead of silently sending it to the wrong route.
  const [fileInputKey, setFileInputKey] = useState(0);
  // A 64 MB dry run can outlive a source/file/zone/entity change. Only the
  // answer for the current selection may become the report the operator can
  // commit: a report for the other entity would write that entity's tax on
  // every line while the select shows this one (YUKI-332).
  const inputRevision = useRef(0);

  function chooseFormat(next: LegacyReceiptImportFormat) {
    if (next === format) return;
    inputRevision.current += 1;
    setFormat(next);
    setFileInputKey((key) => key + 1);
    setError(null);
    setReport(null);
    setCreated(null);
    setSkipped(0);
    setSkippedIds([]);
    setPayload(null);
    setFileName(null);
  }

  async function chooseFile(file: File | undefined) {
    const revision = ++inputRevision.current;
    setError(null);
    setReport(null);
    setCreated(null);
    setSkipped(0);
    setSkippedIds([]);
    setPayload(null);
    setFileName(null);
    if (file === undefined) return;

    const readFailed = t('receipts.import.readFailed', 'Could not read the JSON file.');

    try {
      const parsed = JSON.parse(
        (await readFileText(file, readFailed)).replace(/^\uFEFF/, ''),
      ) as unknown;
      if (revision !== inputRevision.current) return;
      if (!isImportPayload(parsed, format)) {
        throw new Error(
          format === 'gp'
            ? t(
                'receipts.import.gp.invalidRoot',
                'The JSON root must contain a Receipts array (a GP receipts export).',
              )
            : t(
                'receipts.import.invalidRoot',
                'The JSON root must contain Receipts and ReceiptsRet arrays.',
              ),
        );
      }
      setPayload(parsed);
      setFileName(file.name);
    } catch (err) {
      if (revision === inputRevision.current) {
        setError(err instanceof Error ? err.message : readFailed);
      }
    }
  }

  const runCommit = async (skipErrors: boolean): Promise<void> => {
    if (payload === null || report === null) return;
    // The report is cleared on every source change, so it answers `format`.
    const source: LegacyReceiptImportSource | null =
      format === 'gp'
        ? { format: 'gp' }
        : report.tradingEntity === undefined
          ? null
          : { tradingEntity: report.tradingEntity };
    if (source === null) return;

    const total = report.receiptCount + report.returnCount;

    const importing = skipErrors ? total - report.rejectedReceiptCount : total;

    if (
      !window.confirm(
        skipErrors
          ? t(
              'receipts.import.confirmSkip',
              'Import {importing} legacy receipts and skip {rejected} that failed validation? Skipped rows can be corrected and submitted later.',
            )
              .replace('{importing}', String(importing))
              .replace('{rejected}', String(report.rejectedReceiptCount))
          : t(
              'receipts.import.confirmAll',
              'Import {total} legacy receipts? Receipt IDs remain protected against duplicate import.',
            ).replace('{total}', String(total)),
      )
    )
      return;

    setError(null);

    try {
      const result = await commit.mutateAsync({
        payload,

        expectedDigest: report.digest,

        skipErrors,

        // Commit the exact zone that produced this report. The digest is bound
        // to it, so a changed override requires a new dry-run.
        timeZone: report.timeZone,
        // Likewise the legal entity of a 1C file (YUKI-332); a GP commit
        // names only its route.
        ...source,
      });

      setCreated(result.created);

      setSkipped(result.skipped);

      setSkippedIds(result.skippedIds);
    } catch (err) {
      setError(extractApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">
          {format === 'gp'
            ? t('receipts.import.gp.title', 'Legacy GP receipt import')
            : t('receipts.import.title', 'Legacy 1C receipt import')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {format === 'gp'
            ? t(
                'receipts.import.gp.intro',
                'One-off migration per store of receipts rung on the previous re:form till. Validate the exact JSON file before committing it; imported sales can then be found at POS by scanning the tax-service QR printed on the receipt. Returns in the file are booked against their sales, so a returned item cannot be returned twice.',
              )
            : t(
                'receipts.import.intro',
                'One-off migration per store. Validate the exact JSON file before committing it; imported sales can then be found at POS by scanning their FiscalLink QR.',
              )}
        </p>
      </header>

      {error !== null && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </div>
      )}
      {created !== null && (
        <div
          role="status"
          className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm"
        >
          {t(
            'receipts.import.complete',
            'Import complete: {created} legacy receipts created',
          ).replace('{created}', String(created))}
          {skipped > 0 &&
            `, ${t(
              'receipts.import.completeSkipped',
              '{skipped} skipped because they failed validation',
            ).replace('{skipped}', String(skipped))}`}
          {'.'}
        </div>
      )}
      {created !== null && skipped > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          {/*
            A partial import must not read like a clean one — the skipped
            receipts are not returnable at the till and somebody has to know.
          */}
          {(skipped === 1
            ? t(
                'receipts.import.skippedWarnOne',
                '{count} receipt was skipped and is NOT imported. They cannot be returned at a till until the underlying data is corrected and imported for that store.',
              )
            : t(
                'receipts.import.skippedWarnMany',
                '{count} receipts were skipped and are NOT imported. They cannot be returned at a till until the underlying data is corrected and imported for that store.',
              )
          ).replace('{count}', String(skipped))}
          {skippedIds.length > 0 && (
            <span className="mt-2 block font-mono text-xs">
              {t('receipts.import.skippedIds', 'Skipped IDs:')} {skippedIds.join(', ')}
            </span>
          )}
        </div>
      )}

      <section className="space-y-3 rounded-md border bg-background p-5">
        <fieldset
          className="flex flex-wrap gap-4 text-sm"
          disabled={created !== null || commit.isPending}
        >
          <legend className="mb-1 text-muted-foreground">
            {t('receipts.import.format', 'Source system')}
          </legend>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="legacy-import-format"
              checked={format === '1c'}
              onChange={() => chooseFormat('1c')}
            />
            {t('receipts.import.format1c', '1C export')}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="legacy-import-format"
              checked={format === 'gp'}
              onChange={() => chooseFormat('gp')}
            />
            {t('receipts.import.formatGp', 'GP export (previous re:form till)')}
          </label>
        </fieldset>
        <h2 className="text-sm font-medium">
          {format === 'gp'
            ? t('receipts.import.gp.step1', '1 · Select the GP receipts JSON export')
            : t('receipts.import.step1', '1 · Select the 1C JSON export')}
        </h2>
        <input
          key={fileInputKey}
          aria-label={
            format === 'gp'
              ? t('receipts.import.gp.fileAria', 'GP JSON file')
              : t('receipts.import.fileAria', '1C JSON file')
          }
          type="file"
          disabled={commit.isPending}
          accept=".json,application/json"
          onChange={(event) => void chooseFile(event.target.files?.[0])}
        />
        {fileName !== null && (
          <p className="text-xs text-muted-foreground">
            {t('receipts.import.selected', 'Selected: {name}').replace('{name}', fileName)}
          </p>
        )}
        <label className="flex max-w-sm flex-col gap-1 text-sm">
          <span className="text-muted-foreground">
            {t(
              'receipts.import.timeZoneLabel',
              'Optional IANA time-zone override (blank uses every department location)',
            )}
          </span>
          <Input
            value={timeZone}
            disabled={commit.isPending}
            onChange={(event) => {
              inputRevision.current += 1;
              setTimeZone(event.target.value);
              // The digest and every converted timestamp are zone-bound.
              setReport(null);
            }}
            placeholder="Europe/Kyiv"
          />
        </label>
        {format === '1c' && (
          <label className="flex max-w-sm flex-col gap-1 text-sm">
            <span className="text-muted-foreground">
              {t(
                'receipts.import.entityLabel',
                'What the stores traded as when 1C rang these receipts',
              )}
            </span>
            <select
              className="h-10 rounded-md border border-input bg-background px-2 text-sm"
              value={tradingEntity}
              disabled={commit.isPending}
              onChange={(event) => {
                inputRevision.current += 1;
                setTradingEntity(event.target.value as LegacyTradingEntity | '');
                // The digest and every line's register tax are entity-bound.
                setReport(null);
              }}
            >
              <option value="">{t('receipts.import.entityChoose', 'Choose…')}</option>
              <option value="individualEntrepreneur">
                {entityLabel('individualEntrepreneur')}
              </option>
              <option value="llc">{entityLabel('llc')}</option>
            </select>
            <span className="text-xs text-muted-foreground">
              {t(
                'receipts.import.entityHelp',
                'The 1C export carries no VAT. A till returns each imported line under the tax register department this decides, so it must match how the sale was fiscalised.',
              )}
            </span>
          </label>
        )}
        <Button
          disabled={
            payload === null ||
            (format === '1c' && tradingEntity === '') ||
            dryRun.isPending ||
            commit.isPending ||
            created !== null
          }
          onClick={async () => {
            if (payload === null) return;
            let source: LegacyReceiptImportSource;
            if (format === 'gp') source = { format: 'gp' };
            else if (tradingEntity === '') return;
            else source = { tradingEntity };
            const revision = inputRevision.current;
            setError(null);
            setReport(null);
            try {
              const override = timeZone.trim();
              const result = await dryRun.mutateAsync({
                payload,
                ...(override.length === 0 ? {} : { timeZone: override }),
                ...source,
              });
              if (revision === inputRevision.current) setReport(result);
            } catch (err) {
              if (revision === inputRevision.current) setError(extractApiError(err));
            }
          }}
        >
          {dryRun.isPending
            ? t('receipts.import.validating', 'Validating…')
            : t('receipts.import.dryRun', 'Run dry validation')}
        </Button>
      </section>

      {report !== null && (
        <section className="space-y-4 rounded-md border bg-background p-5">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-medium">
              {t('receipts.import.step2', '2 · Validation result')}
            </h2>
            <Badge variant={report.canCommit ? 'success' : 'destructive'}>
              {report.canCommit
                ? t('receipts.import.ready', 'Ready to commit')
                : t('receipts.import.errorCount', '{count} errors').replace(
                    '{count}',
                    String(report.errorCount),
                  )}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('receipts.import.timeZoneNote', 'Receipt timestamps validated in')}{' '}
            <code>{report.timeZone}</code>.
            {report.tradingEntity !== undefined && (
              <>
                {' '}
                {t('receipts.import.entityNote', 'Register tax validated as:')}{' '}
                {entityLabel(report.tradingEntity)}.
              </>
            )}
          </p>
          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-muted-foreground">{t('receipts.import.sales', 'Sales')}</dt>
              <dd className="font-medium">{report.receiptCount}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('receipts.import.returns', 'Returns')}</dt>
              <dd className="font-medium">{report.returnCount}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('receipts.import.stores', 'Stores')}</dt>
              <dd className="font-medium">{report.departmentCodes.join(', ')}</dd>
            </div>
          </dl>
          {report.errors.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH>{t('receipts.import.colReceipt', 'Receipt')}</TH>
                  <TH>{t('receipts.import.colField', 'Field')}</TH>
                  <TH>{t('receipts.import.colError', 'Error')}</TH>
                </TR>
              </THead>
              <TBody>
                {report.errors.map((entry, index) => (
                  <TR key={`${entry.sourceId}-${entry.path}-${index}`}>
                    <TD>{entry.sourceId}</TD>
                    <TD>{entry.path}</TD>
                    <TD>{entry.message}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          {report.errorsTruncated && (
            <p className="text-xs text-destructive">
              {t('receipts.import.truncated', 'Showing the first {shown} of {total} errors.')
                .replace('{shown}', String(report.errors.length))
                .replace('{total}', String(report.errorCount))}
            </p>
          )}
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={
                  !report.canCommit || commit.isPending || created !== null || payload === null
                }
                onClick={() => void runCommit(false)}
              >
                {commit.isPending
                  ? t('receipts.import.importing', 'Importing…')
                  : t('receipts.import.commit', 'Commit one-off import')}
              </Button>
              {/*
                Refusing a whole file over a handful of rows that reference
                products absent from the catalogue left the operator hand-editing
                JSON. This commits the rest and reports exactly what it dropped.
              */}
              {report.errorCount > 0 && (
                <Button
                  variant="outline"
                  disabled={
                    commit.isPending ||
                    created !== null ||
                    payload === null ||
                    report.rejectedReceiptCount >= report.receiptCount + report.returnCount
                  }
                  onClick={() => void runCommit(true)}
                >
                  {commit.isPending
                    ? t('receipts.import.importing', 'Importing…')
                    : t(
                        'receipts.import.skipAndCommit',
                        'Skip errors and commit ({count} skipped)',
                      ).replace('{count}', String(report.rejectedReceiptCount))}
                </Button>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function isImportPayload(
  value: unknown,
  format: LegacyReceiptImportFormat,
): value is LegacyReceiptImportPayload {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  if (!Array.isArray(candidate.Receipts)) return false;
  // A GP export keeps its returns inside `Receipts`; a 1C file splits them out.
  return format === 'gp' || Array.isArray(candidate.ReceiptsRet);
}

function readFileText(file: File, failureMessage: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(failureMessage));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsText(file);
  });
}
